/* eslint-disable no-console */
/// <reference types="vite/client" />
// src/utils/logger.ts - Proper Pino Browser Implementation
import pino from 'pino';

// Generate a compact, unique trace ID per page load (one per browser tab session)
// Format: <base36-timestamp>-<random> (~13 chars)
function generateTraceId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 7);
  return `${timestamp}-${random}`;
}

export const traceId = generateTraceId();

// Shared type definitions for web worker communication
// Configuration types
export type WorkerConfig = {
  backendUrl: string;
  batchSize: number;
  flushInterval: number;
  maxRetries?: number;
};

// Core data types
export type LogEntry = {
  timestamp: number;
  url: string;
  userAgent: string;
  traceId: string;
  level?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  messages?: any[]; // Keep original Pino messages array
  context?: Record<string, string | number | boolean | null>;
};

// ===== INCOMING MESSAGES (Main thread -> Worker) =====
export type IncomingMessage =
  | { type: 'configure'; config: WorkerConfig }
  | { type: 'log'; log: LogEntry }
  | { type: 'flush' }
  | { type: 'ping' };

// ===== OUTGOING MESSAGES (Worker -> Main thread) =====
export type OutgoingMessage =
  | { type: 'batch_sent'; count: number; success: boolean }
  | { type: 'batch_error'; error: string; logs: LogEntry[] }
  | { type: 'configured'; config: WorkerConfig }
  | { type: 'pong'; queueLength: number; isProcessing: boolean }
  | { type: 'error'; message: string }
  | { type: 'worker_ready'; timestamp: string };

// We'll initialize this once the app config is fully loaded
let logWorkerDestinationUrl: string | undefined;

// Function to safely get the worker URL from app config
function getWorkerUrl() {
  if (typeof window !== 'undefined' && window.APP_CONFIG) {
    const url = window.APP_CONFIG.LOG_WORKER_DESTINATION_URL;
    // Treat "undefined" string as undefined
    return url && url !== 'undefined' ? url : undefined;
  }
  return undefined;
}

// Try to get the URL immediately
logWorkerDestinationUrl = getWorkerUrl();

// Set up a listener to check again when DOM is fully loaded
if (
  typeof window !== 'undefined' &&
  (!logWorkerDestinationUrl || logWorkerDestinationUrl === 'undefined')
) {
  window.addEventListener('DOMContentLoaded', () => {
    // Try again after DOM is loaded
    logWorkerDestinationUrl = getWorkerUrl();

    // Initialize worker if URL is now available
    if (logWorkerDestinationUrl && logWorkerDestinationUrl !== 'undefined') {
      initializeWorker();
    }
  });
}

// Use a consistent path for the worker file

// Web Worker for background log processing
let logWorker: Worker | null = null;
let workerReadyPromise: Promise<void> | null = null;

// Initialize Web Worker (only if destination URL is configured)
function initializeWorker() {
  // Only initialize if we have a valid destination URL
  if (!logWorkerDestinationUrl) {
    return;
  }

  try {
    // In development, use the TypeScript file directly; in production, use the built file
    if (import.meta.env.DEV) {
      logWorker = new Worker(new URL('../workers/log-worker.ts', import.meta.url), {
        type: 'module',
      });
    } else {
      logWorker = new Worker('/assets/log-worker.js');
    }

    // Create promise that resolves when worker is configured and ready
    workerReadyPromise = new Promise((resolve) => {
      const handleMessage = (event: MessageEvent<OutgoingMessage>) => {
        if (event.data.type === 'configured') {
          resolve();
        }
      };
      logWorker?.addEventListener('message', handleMessage);
    });

    logWorker.addEventListener('message', (event: MessageEvent<OutgoingMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'worker_ready':
          logWorker?.postMessage({
            type: 'configure',
            config: {
              backendUrl: logWorkerDestinationUrl,
              batchSize: 10,
              flushInterval: 2000,
              maxRetries: 3,
            },
          });
          break;

        case 'configured':
          break;

        case 'batch_sent':
          break;

        case 'batch_error':
          console.error('Log worker error:', message.error);
          break;
      }
    });

    logWorker.addEventListener('error', (error) => {
      console.error('Log worker error:', error);
      // Log more detailed information about the error
      if (error instanceof ErrorEvent) {
        console.error('Worker error details:', {
          message: error.message,
          filename: error.filename,
          lineno: error.lineno,
          colno: error.colno,
        });
      } else {
        console.error('Worker error (non-ErrorEvent):', error);
      }
      workerReadyPromise = null;
    });
  } catch (error) {
    console.error('Failed to initialize log worker:', error);
  }
}

// Send log to worker (wait for ready if needed)

const sendToWorker = async (
  timestamp: number,
  level: string,
  messages: unknown[],
  context: Record<string, string | number | boolean | null>,
) => {
  if (!logWorker || !workerReadyPromise) return;

  // Wait for worker to be configured before sending
  await workerReadyPromise;

  const logEntry: LogEntry = {
    timestamp,
    messages,
    level,
    context,
    traceId,
    url: typeof window !== 'undefined' ? window.location.href : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
  };

  logWorker.postMessage({
    type: 'log',
    log: logEntry,
  });
};

// Development: Custom console styling (no Pino)
const createDevLogger = () => {
  const createMethod = (methodName: string, emoji: string, bgColor: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (...msgs: any[]) => {
      const consoleMethod = methodName as keyof Console;
      const timestamp = new Date().toLocaleTimeString();

      // Pass objects as separate arguments - console handles them natively
      (console[consoleMethod] as Function)(
        `%c${emoji} ${methodName.toUpperCase()}%c %c${timestamp}%c`,
        `background: ${bgColor}; color: white; font-weight: bold; padding: 2px 6px; border-radius: 3px;`,
        'color: inherit;',
        'color: #666; font-size: 0.85em;',
        'color: inherit;',
        ...msgs,
      );
    };
  };

  return {
    info: createMethod('info', 'ℹ️', '#10b981'),
    error: createMethod('error', '❌', '#ef4444'),
    warn: createMethod('warn', '⚠️', '#f59e0b'),
    debug: createMethod('debug', '🐛', '#3b82f6'),
    trace: createMethod('trace', '🔍', '#6b7280'),
    log: createMethod('info', 'ℹ️', '#10b981'), // log maps to info
    child: () => createDevLogger(),
  };
};

// Production: Proper Pino Browser with worker transport
const createPinoLogger = () => {
  // Worker will be initialized by checkAppConfigLoaded

  return pino({
    level: 'info',
    browser: {
      asObject: true, // Proper Pino browser format
      write: () => {}, // Disable default console output
      transmit: {
        level: 'info',
        send: function (level: pino.Level, logEvent: pino.LogEvent) {
          // LR at some point we need to determine context from logEvent.bindings
          if (logWorker && workerReadyPromise) {
            // Send raw messages array, let backend handle formatting
            void sendToWorker(logEvent.ts, level, logEvent.messages, {});
          } else {
            console.warn('Log worker not ready:', logEvent);
          }
        },
      },
    },
  });
};

// Create the appropriate logger based on whether we have a worker URL
const baseLogger = logWorkerDestinationUrl ? createPinoLogger() : createDevLogger();

// Initialize worker if we have a destination URL
if (logWorkerDestinationUrl) {
  initializeWorker();
}

// Ensure log method exists for backward compatibility in both environments
const logger = Object.assign(baseLogger, {
  // Add log method that maps to info (for backward compatibility)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  log: (...args: any[]) => {
    return baseLogger.info(args);
  },

  // Worker control methods (for debugging/testing)
  flushLogs: () => {
    if (logWorker) {
      logWorker.postMessage({ type: 'flush' });
    }
  },

  getWorkerStatus: () => {
    return {
      hasWorker: !!logWorker,
      isReady: workerReadyPromise,
      isPino: !!logWorkerDestinationUrl,
    };
  },
});

// Make logger available in browser console for debugging
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).logger = logger;
}

export default logger;
